import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import queryString from "query-string";

import { swSettings } from "app/common/services/swSettings";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import type { ISort } from "app/pages/keyword-research/keyword-analysis/common-components/domains-table/Table";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { DownloadExcelContainer, HeaderStyle, Right, StyledBoxSubtitle, StyledHeaderTitle } from "./styledComponents";

const TABLE_DASHBOARD_TYPE = "TableKeywordAnalysisDashboard";
interface ITableTopProps {
    filtersStateObject: Record<string, any>;
    sort: ISort;
    tableEndpoint: string;
    dashboardMetric: string;
    tableHeaderTooltip: string;
    totalCount: number;
    hideAddToDashboard?: boolean;
    hideExcel?: boolean;
}
export const TableTop: FC<ITableTopProps> = ({
    filtersStateObject,
    sort,
    tableEndpoint,
    dashboardMetric,
    tableHeaderTooltip,
    hideAddToDashboard = false,
    hideExcel = false,
    totalCount,
}) => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const { params } = useCurrentStateAndParams();
    const { webSource, selectedPageTab, category, funcFlag, domains } = params;

    const filtersParams = {
        category: filtersStateObject.category,
        funcFlag: filtersStateObject.funcFlag,
        orderBy: filtersStateObject.orderBy,
        sourceType: SourceTypeMap[selectedPageTab as keyof typeof SourceTypeMap],
        domains,
    };

    const excelLink = useMemo(() => {
        const queryStringParams = queryString.stringify({
            ...filtersStateObject,
            ...(sort?.field ? { sort: sort.field } : {}),
            ...(sort?.sortDirection ? { asc: sort.sortDirection === "asc" } : {}),
        });
        return `/${tableEndpoint}/Excel?${queryStringParams}`.replaceAll("//", "/");
    }, [filtersStateObject, sort]);
    const excelAllowed = swSettings.current.resources.IsExcelAllowed;

    return (
        <HeaderStyle>
            <div>
                <StyledHeaderTitle>
                    <BoxTitle tooltip={translate(tableHeaderTooltip)}>
                        {translate(
                            `${SourceTypeMap[params.selectedPageTab as keyof typeof SourceTypeMap]}.keywords.2.table.title`,
                        )}
                    </BoxTitle>
                </StyledHeaderTitle>
                <StyledBoxSubtitle>
                    <BoxSubtitle filters={subtitleFilters} />
                </StyledBoxSubtitle>
            </div>
            <Right>
                <FlexRow>
                    {!hideExcel ? (
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                PaidExcel={isPaidExcelAllowed()}
                                totalCount={totalCount}
                                Excel={true}
                                downloadUrl={excelLink}
                                excelLocked={!excelAllowed}
                            />
                        </DownloadExcelContainer>
                    ) : null}

                    {!hideAddToDashboard ? (
                        <AddToDashboard
                            webSource={webSource}
                            modelType="fromKeyword"
                            metric={dashboardMetric}
                            type={TABLE_DASHBOARD_TYPE}
                            filters={filtersParams}
                        />
                    ) : null}
                </FlexRow>
            </Right>
        </HeaderStyle>
    );
};
